/**
 * openrag-chat component
 *
 * Renders a simulated OpenRAG UI with an auto-playing multi-turn D&D conversation.
 * Each turn shows a tool call in running → done state, followed by thinking dots
 * and a typewriter AI response. Desktop only. Returns null on mobile/touch devices.
 *
 * @param {object} props - (unused, no props required)
 * @param {number} index - Panel index for stagger animation
 * @returns {HTMLElement|null}
 */

import './openrag-chat.css';
import { PANEL_ANIMATION_DELAY_MS } from '../constants.js';

const DESKTOP_QUERY = '(pointer: fine) and (min-width: 769px)';

// ---------------------------------------------------------------------------
// Teams select data
// ---------------------------------------------------------------------------

const TEAMS = [
  ['all', 'All teams'],
  ['thornwall', 'Thornwall Campaign'],
  ['ironbastion', 'Iron Bastion Siege'],
  ['bestiary', 'Bestiary Guild'],
  ['loot', 'Loot & Artifacts'],
];

const MODELS = [
  ['gpt53', 'GPT-5.3'],
  ['sonnet-46', 'Sonnet 4.6'],
  ['gemini-35', 'Gemini 3.5'],
];

const KNOWLEDGE_FILTERS_ALL = [
  ['campaign-prep', 'Campaign Prep'],
  ['session-logs', 'Session Logs'],
  ['bestiary', 'Bestiary'],
  ['loot-tables', 'Loot Tables'],
  ['world-notes', 'World Notes'],
];

const FILTER_COLORS = {
  'campaign-prep': 'var(--color-violet-900)',
  'session-logs': 'var(--color-sky-900)',
  bestiary: 'var(--color-yellow-900)',
  'loot-tables': 'var(--color-emerald-900)',
  'world-notes': 'var(--color-rose-900)',
};

const FILTER_TEXT_COLORS = {
  'campaign-prep': 'var(--color-violet-300)',
  'session-logs': 'var(--color-sky-300)',
  bestiary: 'var(--color-yellow-400)',
  'loot-tables': 'var(--color-emerald-400)',
  'world-notes': 'var(--color-rose-300)',
};

const _orcSelectClosers = new Set();

// ---------------------------------------------------------------------------
// Conversation script
// ---------------------------------------------------------------------------

const CONVERSATION = [
  {
    user: 'Which NPCs were introduced in the Thornwall Keep session?',
    tool: {
      name: 'Search knowledge filters',
      query: '"Campaign Prep · Thornwall Keep"',
    },
    response: [
      {
        type: 'para',
        text: 'From your Campaign Prep notes, three NPCs were introduced during the Thornwall Keep session.',
      },
      {
        type: 'list',
        items: [
          'Lord Aldric Brennan — the keep\'s steward. Guard logs suggest he\'s been skimming tribute from nearby settlements.',
          'Sister Vane — a cleric of Sorrow who arrived unannounced with a sealed letter addressed to the Brennan family.',
          'The Unnamed Prisoner — referenced in cell block records but not yet encountered by the party.',
        ],
      },
      {
        type: 'para',
        text: 'Want me to pull full character profiles for any of them?',
      },
    ],
  },
  {
    user: 'Suggest an ambush for the road back to Millhaven.',
    tool: {
      name: 'Generate encounter',
      query: '"road encounter · outdoors · CR 3–5"',
    },
    response: [
      {
        type: 'para',
        text: 'Based on the current arc, here\'s an ambush that ties back to Brennan\'s network:',
      },
      {
        type: 'list',
        items: [
          '4 Guard — staged in the treeline with crossbows, wearing no insignia.',
          '1 Veteran (Brennan\'s enforcer) — carrying a sealed arrest warrant for the party.',
        ],
      },
      {
        type: 'para',
        text: 'The veteran will attempt to parley first. A DC\u00a012 Persuasion check reveals who dispatched them.',
      },
    ],
  },
  {
    user: 'Write a recap of tonight\'s session.',
    tool: {
      name: 'Retrieve session log',
      query: '"Session 7 · Thornwall Keep · draft"',
    },
    response: [
      {
        type: 'para',
        text: 'Here\'s a draft recap based on tonight\'s events:',
      },
      {
        type: 'list',
        items: [
          'The party investigated Thornwall Keep and uncovered evidence of Brennan\'s tribute skimming.',
          'Sister Vane was identified as a potential informant, carrying a sealed letter of unknown origin.',
          'The Unnamed Prisoner in the cell block remains unencountered — a loose thread for next session.',
        ],
      },
      {
        type: 'para',
        text: 'Want me to expand any section or add the road ambush as a closing cliffhanger?',
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// Timing constants
// ---------------------------------------------------------------------------

const CHARS_PER_SECOND = 250; // typewriter speed — matches markdown source typewriter
const BLOCK_PAUSE_MS = 80; // pause between paragraphs / list items
const USER_DELAY_MS = 900; // before first user message each loop
const TOOL_DELAY_MS = 600; // after user msg, before tool call card appears
const TOOL_DURATION_MS = 1800; // how long tool call shows as "running"
const TOOL_COMPLETE_MS = 450; // pause after tool done before AI response
const BETWEEN_TURNS_MS = 2000; // pause between conversation turns
const LOOP_PAUSE_MS = 5500; // pause after final response before reset
const MAX_FILTERS = 3; // max knowledge filter tags in input bar

// ---------------------------------------------------------------------------
// Entry point
// ---------------------------------------------------------------------------

export function createOpenragChat(props, index) {
  try {
    const isDesktop = window.matchMedia(DESKTOP_QUERY).matches;
    if (!isDesktop) return null;

    const panel = document.createElement('div');
    panel.className =
      'portfolio-panel portfolio-panel--component portfolio-panel--no-border';
    panel.classList.remove('skeleton-loading');

    buildScene(panel);
    setTimeout(
      () => panel.classList.add('animated'),
      PANEL_ANIMATION_DELAY_MS * (index || 0),
    );
    return panel;
  } catch (err) {
    console.error('[openrag-chat] render failed:', err);
    return null;
  }
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function el(tag, cls, text) {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text !== undefined) e.textContent = text;
  return e;
}

// ---------------------------------------------------------------------------
// SVG icons
// ---------------------------------------------------------------------------

const SVG_LOGO =
  '<svg width="15" height="14" viewBox="0 0 15 14" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<path fill-rule="evenodd" clip-rule="evenodd" d="M4.21275 4.02739H5.93618V3.83557C5.9362 1.71725 7.65082 6.88856e-06 9.76593 0H10.1489V4.02739H14.1667C14.6269 4.02739 15 4.4016 15 4.86321V10.1644C15 12.2828 13.2853 14 11.1701 14H8.62182e-05C8.62182e-05 14 0 4.8694 0 3.83557C2.57378e-05 1.71725 1.71465 1.10809e-05 3.82975 0H4.21275V4.02739ZM5.95561 6.58444C5.18009 6.58448 4.55139 7.38589 4.55139 8.37443C4.55139 9.36297 5.18009 10.1644 5.95561 10.1644C6.73115 10.1644 7.35992 9.36299 7.35993 8.37443C7.35993 7.38587 6.73115 6.58444 5.95561 6.58444Z" fill="white"/>' +
  '</svg>';

const SVG_EXPAND =
  '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="m7 15 5 5 5-5"/>' +
  '<path d="m7 9 5-5 5 5"/>' +
  '</svg>';

const SVG_CLOSE =
  '<svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="M18 6 6 18"/>' +
  '<path d="m6 6 12 12"/>' +
  '</svg>';

const SVG_CHAT =
  '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="M22 17a2 2 0 0 1-2 2H6.828a2 2 0 0 0-1.414.586l-2.202 2.202A.71.71 0 0 1 2 21.286V5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2z"/>' +
  '</svg>';

const SVG_KNOWLEDGE =
  '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="m16 6 4 14"/>' +
  '<path d="M12 6v14"/>' +
  '<path d="M8 8v12"/>' +
  '<path d="M4 4v16"/>' +
  '</svg>';

const SVG_ARTIFACTS =
  '<svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="M8.3 10a.7.7 0 0 1-.626-1.079L11.4 3a.7.7 0 0 1 1.198-.043L16.3 8.9a.7.7 0 0 1-.572 1.1Z"/>' +
  '<rect x="3" y="14" width="7" height="7" rx="1"/>' +
  '<circle cx="17.5" cy="17.5" r="3.5"/>' +
  '</svg>';

const SVG_PLUS =
  '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="M5 12h14"/>' +
  '<path d="M12 5v14"/>' +
  '</svg>';

const SVG_DOTS_V =
  '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg">' +
  '<circle cx="12" cy="12" r="1"/>' +
  '<circle cx="12" cy="5" r="1"/>' +
  '<circle cx="12" cy="19" r="1"/>' +
  '</svg>';

const SVG_SEND =
  '<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="m5 12 7-7 7 7"/>' +
  '<path d="M12 19V5"/>' +
  '</svg>';

const SVG_AVATAR =
  '<svg width="24" height="22" viewBox="0 0 24 22" fill="none" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="M17.3138 12.0214C19.2536 12.0214 20.7275 13.7707 20.4039 15.6887L19.3389 22H16.6351C16.6326 21.9913 16.6351 17.4429 13.0136 17.4429C7.78163 17.4429 5.43131 21.9997 5.43106 22H2.84551L3.86659 15.9492C4.24928 13.6813 6.20812 12.0215 8.50171 12.0214H17.3138Z" fill="#773EFF"/>' +
  '<path d="M0.0810011 9.61917C-0.430828 7.07193 1.60391 6.42555 2.78974 6.46534C3.04513 6.47391 3.20321 6.71823 3.15715 6.9703C2.88221 8.47522 2.56371 10.8544 4.18167 11.6962C4.38774 11.8035 4.38791 12.1836 4.15642 12.2012C2.56639 12.322 0.537604 11.8915 0.0810011 9.61917Z" fill="#773EFF"/>' +
  '<path fill-rule="evenodd" clip-rule="evenodd" d="M15.8863 3.02876H17.1826V2.88451C17.1826 1.29144 18.4722 5.18048e-06 20.0631 0H20.3512V3.02876H23.3732C23.7194 3.02876 24 3.31018 24 3.65733V7.64406C24 9.23713 22.7102 10.5286 21.1193 10.5286H12.7177C12.7177 10.5286 12.7176 3.66199 12.7176 2.88451C12.7176 1.29144 14.0073 8.33331e-06 15.5982 0H15.8863V3.02876ZM17.1972 4.95177C16.6139 4.9518 16.141 5.55449 16.141 6.29791C16.141 7.04134 16.6139 7.64403 17.1972 7.64406C17.7805 7.64406 18.2534 7.04135 18.2534 6.29791C18.2534 5.55448 17.7805 4.95177 17.1972 4.95177Z" fill="#773EFF"/>' +
  '</svg>';

const SVG_TOOL =
  '<svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" xmlns="http://www.w3.org/2000/svg">' +
  '<path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>' +
  '<circle cx="12" cy="12" r="4"/>' +
  '</svg>';

// ---------------------------------------------------------------------------
// Scene builders
// ---------------------------------------------------------------------------

function buildScene(panel) {
  const app = el('div', 'orc-app');
  app.appendChild(buildTopbar());

  const body = el('div', 'orc-body');
  body.appendChild(buildSidebar());

  const main = el('div', 'orc-main');
  const chatArea = el('div', 'orc-chat-area');
  const messages = el('div', 'orc-messages');
  chatArea.appendChild(messages);
  main.appendChild(chatArea);
  main.appendChild(buildInputBar());
  body.appendChild(main);

  app.appendChild(body);
  panel.appendChild(app);

  // Close any open select on click outside the trigger
  app.addEventListener('click', () => {
    _orcSelectClosers.forEach((fn) => fn());
  });

  startConversation(messages);
}

function makeOrcSelect(options) {
  let selected = options[0][0];
  let isOpen = false;
  let listEl = null;

  const wrap = el('div', 'orc-select-wrap');

  const trigger = el('button', 'orc-select');
  trigger.type = 'button';

  const labelEl = el('span', null, options[0][1]);
  trigger.appendChild(labelEl);

  const chevWrap = el('span', 'orc-teams-chevron');
  chevWrap.innerHTML = SVG_EXPAND;
  trigger.appendChild(chevWrap);

  wrap.appendChild(trigger);

  function close() {
    if (!isOpen) return;
    isOpen = false;
    _orcSelectClosers.delete(close);
    if (listEl) {
      listEl.remove();
      listEl = null;
    }
  }

  function open() {
    // Close all other open selects
    _orcSelectClosers.forEach((fn) => fn());
    isOpen = true;
    _orcSelectClosers.add(close);

    listEl = el('div', 'orc-select-list');

    options.forEach(([value, text]) => {
      const item = el(
        'div',
        'orc-select-item' +
          (value === selected ? ' orc-select-item--selected' : ''),
        text,
      );
      item.addEventListener('click', () => {
        selected = value;
        labelEl.textContent = text;
        close();
      });
      listEl.appendChild(item);
    });

    wrap.appendChild(listEl);
  }

  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    if (isOpen) close();
    else open();
  });

  // Close on outside click — listen on the orc-app ancestor
  wrap._orcSelectClose = close;

  return wrap;
}

function makeModelSelect(options) {
  let selected = options[0][0];
  let isOpen = false;
  let listEl = null;

  const wrap = el('div', 'orc-select-wrap');

  const trigger = el('button', 'orc-model-btn');
  trigger.type = 'button';

  const labelEl = el('span', null, options[0][1]);
  trigger.appendChild(labelEl);

  const chevWrap = el('span', 'orc-teams-chevron');
  chevWrap.innerHTML = SVG_EXPAND;
  trigger.appendChild(chevWrap);

  wrap.appendChild(trigger);

  function close() {
    if (!isOpen) return;
    isOpen = false;
    _orcSelectClosers.delete(close);
    if (listEl) {
      listEl.remove();
      listEl = null;
    }
  }

  function open() {
    _orcSelectClosers.forEach((fn) => fn());
    isOpen = true;
    _orcSelectClosers.add(close);

    listEl = el('div', 'orc-select-list orc-select-list--up');
    options.forEach(([value, text]) => {
      const item = el(
        'div',
        'orc-select-item' +
          (value === selected ? ' orc-select-item--selected' : ''),
        text,
      );
      item.addEventListener('click', () => {
        selected = value;
        labelEl.textContent = text;
        close();
      });
      listEl.appendChild(item);
    });

    wrap.appendChild(listEl);
  }

  trigger.addEventListener('click', (e) => {
    e.stopPropagation();
    if (isOpen) close();
    else open();
  });

  return wrap;
}

function buildTopbar() {
  const bar = el('div', 'orc-topbar');

  const logo = el('div', 'orc-logo');
  logo.innerHTML = SVG_LOGO;
  bar.appendChild(logo);

  bar.appendChild(el('span', 'orc-topbar-sep', '/'));
  bar.appendChild(makeOrcSelect(TEAMS));
  return bar;
}

function buildSidebar() {
  const sidebar = el('div', 'orc-sidebar');

  const nav = el('div', 'orc-nav');
  [
    { icon: SVG_CHAT, label: 'Chat', active: true },
    { icon: SVG_KNOWLEDGE, label: 'Knowledge' },
    { icon: SVG_ARTIFACTS, label: 'Artifacts' },
  ].forEach(({ icon, label, active }) => {
    const item = el(
      'div',
      'orc-nav-item' + (active ? ' orc-nav-item--active' : ''),
    );
    const iconEl = el('span', 'orc-nav-icon');
    iconEl.innerHTML = icon;
    item.appendChild(iconEl);
    item.appendChild(el('span', 'orc-nav-label', label));
    nav.appendChild(item);
  });
  sidebar.appendChild(nav);

  sidebar.appendChild(el('div', 'orc-divider'));

  const convosHdr = el('div', 'orc-convos-header');
  convosHdr.appendChild(el('span', 'orc-convos-label', 'Conversations'));
  const plusBtn = el('button', 'orc-icon-btn');
  plusBtn.type = 'button';
  plusBtn.innerHTML = SVG_PLUS;
  convosHdr.appendChild(plusBtn);
  sidebar.appendChild(convosHdr);

  const list = el('div', 'orc-convos-list');
  [
    { label: 'Campaign Prep', active: true },
    { label: 'Loot & Treasure Tables' },
    { label: 'Bestiary Quick Reference' },
    { label: 'Session Recap Drafts' },
  ].forEach(({ label, active }) => {
    const item = el(
      'div',
      'orc-convo-item' + (active ? ' orc-convo-item--active' : ''),
    );
    item.appendChild(el('span', 'orc-convo-label', label));
    if (active) {
      const menuBtn = el('button', 'orc-icon-btn orc-convo-menu');
      menuBtn.type = 'button';
      menuBtn.innerHTML = SVG_DOTS_V;
      item.appendChild(menuBtn);
    }
    list.appendChild(item);
  });
  sidebar.appendChild(list);

  return sidebar;
}

function buildInputBar() {
  const bar = el('div', 'orc-input-bar');

  // --- Placeholder row ---
  const inputFake = el('div', 'orc-input-fake');
  inputFake.appendChild(
    el('span', 'orc-input-placeholder', 'Ask a question...'),
  );
  bar.appendChild(inputFake);

  // --- Toolbar: model select + separator + filter tags + send ---
  const toolbar = el('div', 'orc-input-toolbar');
  toolbar.appendChild(makeModelSelect(MODELS));

  let activeFilters = ['campaign-prep'];
  let filterPickerEl = null;
  const filtersRow = el('div', 'orc-filters');

  function closeFilterPicker() {
    if (filterPickerEl) {
      filterPickerEl.remove();
      filterPickerEl = null;
      _orcSelectClosers.delete(closeFilterPicker);
    }
  }

  function renderFilters() {
    filtersRow.innerHTML = '';

    activeFilters.forEach((key) => {
      const label = KNOWLEDGE_FILTERS_ALL.find(([k]) => k === key)?.[1] ?? key;
      const tag = el('div', 'orc-filter-tag');
      tag.style.background = FILTER_COLORS[key] ?? 'var(--color-violet-500)';
      tag.style.color = FILTER_TEXT_COLORS[key] ?? 'var(--color-violet-400)';
      tag.appendChild(el('span', null, label));
      const removeBtn = el('button', 'orc-filter-remove');
      removeBtn.type = 'button';
      removeBtn.innerHTML = SVG_CLOSE;
      removeBtn.addEventListener('click', (e) => {
        e.stopPropagation();
        activeFilters = activeFilters.filter((k) => k !== key);
        closeFilterPicker();
        renderFilters();
      });
      tag.appendChild(removeBtn);
      filtersRow.appendChild(tag);
    });

    const addWrap = el('div', 'orc-filter-add-wrap');
    const addBtn = el('button', 'orc-filter-add');
    addBtn.type = 'button';
    addBtn.innerHTML = SVG_PLUS;
    addBtn.disabled = activeFilters.length >= MAX_FILTERS;

    addBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      if (addBtn.disabled) return;
      if (filterPickerEl) {
        closeFilterPicker();
        return;
      }
      const available = KNOWLEDGE_FILTERS_ALL.filter(
        ([k]) => !activeFilters.includes(k),
      );
      if (available.length === 0) return;

      filterPickerEl = el('div', 'orc-filter-picker');
      available.forEach(([filterKey, filterLabel]) => {
        const item = el('div', 'orc-select-item', filterLabel);
        item.addEventListener('click', (e) => {
          e.stopPropagation();
          activeFilters.push(filterKey);
          closeFilterPicker();
          renderFilters();
        });
        filterPickerEl.appendChild(item);
      });

      _orcSelectClosers.add(closeFilterPicker);
      addWrap.appendChild(filterPickerEl);
    });

    addWrap.appendChild(addBtn);
    filtersRow.appendChild(addWrap);
  }

  renderFilters();
  toolbar.appendChild(filtersRow);

  // Send button at the trailing end of the toolbar
  const sendBtn = el('button', 'orc-send-btn');
  sendBtn.type = 'button';
  sendBtn.innerHTML = SVG_SEND;
  toolbar.appendChild(sendBtn);

  bar.appendChild(toolbar);

  return bar;
}

// ---------------------------------------------------------------------------
// Conversation animation helpers
// ---------------------------------------------------------------------------

function fadeIn(domEl, durationMs) {
  domEl.style.opacity = '0';
  void domEl.offsetHeight;
  domEl.style.transition = 'opacity ' + durationMs + 'ms ease';
  domEl.style.opacity = '1';
}

function appendUserMsg(container, text) {
  const msg = el('div', 'orc-msg orc-msg--user');
  const avatar = el('div', 'orc-user-avatar');
  const content = el('div', 'orc-user-content', text);
  msg.appendChild(avatar);
  msg.appendChild(content);
  msg.style.opacity = '0';
  msg.style.transform = 'translateY(8px)';
  container.appendChild(msg);
  void msg.offsetHeight;
  msg.style.transition = 'opacity 0.3s ease, transform 0.3s ease';
  msg.style.opacity = '1';
  msg.style.transform = 'translateY(0)';
}

function appendToolCall(container, tool) {
  const row = el('div', 'orc-msg orc-msg--tool');
  const card = el('div', 'orc-tool-call');
  const header = el('div', 'orc-tool-call-header');
  const icon = el('span', 'orc-tool-icon');
  icon.innerHTML = SVG_TOOL;
  const fnEl = el('span', 'orc-tool-fn orc-tool-fn--shimmer', tool.name + '');
  header.appendChild(icon);
  header.appendChild(fnEl);
  card.appendChild(header);
  row.appendChild(card);
  container.appendChild(row);
  fadeIn(row, 200);
  return row;
}

function buildToolCode(tool) {
  const code = el('div', 'orc-tool-code');
  code.appendChild(el('span', 'orc-code-brace', '{'));
  code.appendChild(document.createTextNode(' '));
  code.appendChild(el('span', 'orc-code-key', '"query"'));
  code.appendChild(el('span', 'orc-code-sep', ': '));
  code.appendChild(el('span', 'orc-code-val', tool.query));
  code.appendChild(document.createTextNode(' '));
  code.appendChild(el('span', 'orc-code-brace', '}'));
  return code;
}

function setToolCallDone(toolRow, tool) {
  const fnEl = toolRow.querySelector('.orc-tool-fn');
  if (fnEl) fnEl.classList.remove('orc-tool-fn--shimmer');
  const card = toolRow.querySelector('.orc-tool-call');
  if (card) card.appendChild(buildToolCode(tool));
}

function appendAiTextMsg(container) {
  const msg = el('div', 'orc-msg orc-msg--ai');
  const avatar = el('div', 'orc-avatar');
  avatar.innerHTML = SVG_AVATAR;
  const content = el('div', 'orc-ai-content');
  msg.appendChild(avatar);
  msg.appendChild(content);
  container.appendChild(msg);
  fadeIn(msg, 200);
  return content;
}

// ---------------------------------------------------------------------------
// Typewriter engine — rAF-based with organic stagger, matches markdown typewriter
// ---------------------------------------------------------------------------

// Pre-compute reveal timestamps for each character with natural variation.
// 6% of chars get a micro-pause (3–7× base interval); the rest vary 0.4–1.6×.
function buildTimings(totalChars) {
  const base = 1000 / CHARS_PER_SECOND;
  const timings = new Float32Array(totalChars);
  let t = 0;
  for (let i = 0; i < totalChars; i++) {
    const r = Math.random();
    const mult = r < 0.06 ? 3 + Math.random() * 4 : 0.4 + Math.random() * 1.2;
    t += base * mult;
    timings[i] = t;
  }
  return timings;
}

function typeStr(target, str, onDone) {
  const total = str.length;
  if (total === 0) {
    onDone && onDone();
    return;
  }
  const timings = buildTimings(total);
  let revealed = 0;
  let startTime = null;

  function step(ts) {
    if (startTime === null) startTime = ts;
    const elapsed = ts - startTime;
    let want = revealed;
    while (want < total && timings[want] <= elapsed) want++;
    if (want > revealed) {
      target.textContent = str.slice(0, want);
      revealed = want;
    }
    if (revealed < total) {
      requestAnimationFrame(step);
    } else {
      onDone && onDone();
    }
  }

  requestAnimationFrame(step);
}

function typeListItems(ul, items, idx, onDone) {
  if (idx >= items.length) {
    onDone && onDone();
    return;
  }
  const li = document.createElement('li');
  li.className = 'orc-ai-list-item';
  ul.appendChild(li);
  typeStr(li, items[idx], () => {
    setTimeout(() => typeListItems(ul, items, idx + 1, onDone), BLOCK_PAUSE_MS);
  });
}

// blocks: Array of { type: 'para', text } | { type: 'list', items: [] }
function typeContent(contentEl, blocks, onDone) {
  let bIdx = 0;

  function nextBlock() {
    if (bIdx >= blocks.length) {
      onDone && onDone();
      return;
    }
    const block = blocks[bIdx++];

    if (bIdx > 1) {
      const sp = document.createElement('div');
      sp.style.height = '10px';
      contentEl.appendChild(sp);
    }

    if (block.type === 'para') {
      const p = document.createElement('p');
      p.style.cssText = 'margin:0;line-height:1.65;font-size:13px;';
      contentEl.appendChild(p);
      typeStr(p, block.text, () => setTimeout(nextBlock, BLOCK_PAUSE_MS));
    } else if (block.type === 'list') {
      const ul = document.createElement('ul');
      ul.className = 'orc-ai-list';
      contentEl.appendChild(ul);
      typeListItems(ul, block.items, 0, () =>
        setTimeout(nextBlock, BLOCK_PAUSE_MS),
      );
    }
  }

  nextBlock();
}

// ---------------------------------------------------------------------------
// Conversation orchestration
// ---------------------------------------------------------------------------

function playTurns(messagesEl, turns, idx, onDone) {
  if (idx >= turns.length) {
    onDone();
    return;
  }
  const turn = turns[idx];
  const isLast = idx === turns.length - 1;

  appendUserMsg(messagesEl, turn.user);

  setTimeout(() => {
    const toolRow = appendToolCall(messagesEl, turn.tool);

    setTimeout(() => {
      setToolCallDone(toolRow, turn.tool);

      setTimeout(() => {
        const contentEl = appendAiTextMsg(messagesEl);
        typeContent(contentEl, turn.response, () => {
          if (isLast) {
            onDone();
          } else {
            setTimeout(
              () => playTurns(messagesEl, turns, idx + 1, onDone),
              BETWEEN_TURNS_MS,
            );
          }
        });
      }, TOOL_COMPLETE_MS);
    }, TOOL_DURATION_MS);
  }, TOOL_DELAY_MS);
}

function startConversation(messagesEl) {
  function run() {
    messagesEl.innerHTML = '';

    setTimeout(() => {
      playTurns(messagesEl, CONVERSATION, 0, () => {
        setTimeout(() => {
          messagesEl.style.transition = 'opacity 0.5s ease';
          messagesEl.style.opacity = '0';
          setTimeout(() => {
            messagesEl.style.transition = '';
            messagesEl.style.opacity = '1';
            run();
          }, 600);
        }, LOOP_PAUSE_MS);
      });
    }, USER_DELAY_MS);
  }

  run();
}
